// ÚNICO ponto do frontend autorizado a instanciar @supabase/supabase-js (ADR-054, "Correção e ajustes
// (25/08/2026)", item 1). O padrão geral do projeto continua sendo `client.ts` (fetch puro contra
// Edge Functions) — releia o comentário no topo daquele arquivo antes de mexer aqui.
//
// Escopo autorizado: SÓ `mfaClient.auth.mfa.*` (enroll/challenge/verify/listFactors/
// getAuthenticatorAssuranceLevel) e as RPCs de auditoria MFA (`record_mfa_enrollment_confirmed` e
// `record_mfa_challenge_failure`) — essas RPCs
// é `authenticated` e chamada com o JWT da própria sessão (ver ADR-054), não `service_role`. NENHUMA
// chamada a `.from()`, Storage ou Realtime é autorizada com este cliente. O teste
// `test_frontend_sdk_scope_limited_to_mfa_namespace` (ver MfaEnrollPage.test.tsx) existe para tornar
// essa fronteira grepável e provar que ela não vaza para o resto do app.
//
// A instrução do Paulo para esta fase foi explícita: matrícula de MFA fala direto com o GoTrue via
// SDK, sem Edge Function intermediária (o JWT do chamador já basta para o GoTrue autorizar). Reaproveita
// a mesma fonte de configuração que `client.ts` já usa (variáveis de ambiente Vite), sem inventar uma
// nova.
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export const mfaClient = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    // Este cliente nunca gerencia a sessão do associado (isso é responsabilidade de AuthContext/
    // session.ts, que já guardam o access_token vindo de auth-login em sessionStorage). Desligar
    // persistSession/autoRefreshToken evita que o SDK crie seu próprio armazenamento paralelo de
    // sessão — a única coisa que ele autentica é a própria chamada de auth.mfa.*, usando o JWT que
    // repassamos manualmente logo abaixo (setSession).
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
});

/**
 * As chamadas `auth.mfa.*` do supabase-js operam sobre a sessão interna do próprio client, não sobre
 * um token passado por parâmetro. Como este cliente não persiste sessão (acima), cada tela precisa
 * "emprestar" o access_token/refresh_token já validados por auth-login antes de chamar enroll/
 * challenge/verify. Isso não cria uma segunda fonte de verdade: StoredSession continua sendo a única
 * fonte, este cliente só espelha o token nela por instante da chamada.
 */
export async function setMfaClientSession(accessToken: string, refreshToken: string): Promise<void> {
  const { error } = await mfaClient.auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken,
  });
  if (error) throw error;
}

/**
 * Registra a confirmação usando o JWT recém-emitido pelo GoTrue.
 *
 * O cliente MFA mantém uma sessão efêmera para as chamadas mfa.*. Para a RPC de auditoria,
 * enviamos o access token explicitamente: isso evita que o PostgREST reutilize uma sessão antiga
 * ou caia para o papel anon durante a transição AAL1 -> AAL2.
 */
export async function recordMfaEnrollmentConfirmed(factorId: string, accessToken: string): Promise<{ error: unknown }> {
  const response = await fetch(
    SUPABASE_URL.replace(/\/$/, "") + "/rest/v1/rpc/record_mfa_enrollment_confirmed",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: "Bearer " + accessToken,
      },
      body: JSON.stringify({ p_factor_id: factorId }),
    },
  );

  if (response.ok) return { error: null };
  let error: unknown = null;
  try {
    error = await response.json();
  } catch {
    error = new Error("Falha HTTP " + response.status + " ao registrar auditoria MFA.");
  }
  return { error };
}

export async function recordMfaFactorReplacement(
  oldFactorId: string,
  newFactorId: string,
  accessToken: string,
): Promise<{ error: unknown }> {
  const response = await fetch(
    SUPABASE_URL.replace(/\/$/, "") + "/rest/v1/rpc/record_mfa_factor_replacement",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_PUBLISHABLE_KEY,
        Authorization: "Bearer " + accessToken,
      },
      body: JSON.stringify({ p_old_factor_id: oldFactorId, p_new_factor_id: newFactorId }),
    },
  );

  if (response.ok) return { error: null };
  let error: unknown = null;
  try {
    error = await response.json();
  } catch {
    error = new Error("Falha HTTP " + response.status + " ao registrar troca de fator.");
  }
  return { error };
}

export async function recordMfaChallengeFailure(factorId: string, reason: "invalid_code" | "challenge_expired" | "challenge_unavailable") {
  return mfaClient.rpc("record_mfa_challenge_failure", { p_factor_id: factorId, p_reason: reason });
}
